import * as React from "react"
import { Head, Link, router } from "@inertiajs/react"
import { RefreshCw, Search, Star, Telescope } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"

type AccountRow = {
  id: number
  code: string | null
  name: string
  outlet_group_key: string
  channel_group: string | null
  is_key_account: boolean
  research_enabled: boolean
  status: string
  account_group_name: string | null
  pic_name: string | null
}

type Filters = {
  search: string | null
  key_account: string | null
  research: string | null
  status: string | null
  sort_by: string
  sort_order: string
}

type PageProps = {
  accounts: AccountRow[]
  pagination: { total_count: number; page: number; per_page: number }
  filters: Filters
  stats: { total: number; key_accounts: number; archived: number }
}

const DESCRIPTION =
  "Daftar akun ritel/distribusi yang diturunkan dari master outlet, dengan penanda Key Account, PIC, dan toggle riset 360°."

export default function Index({ accounts, pagination, filters, stats }: PageProps) {
  const [search, setSearch] = React.useState(filters.search ?? "")

  const applyFilters = (next: Partial<Filters> & { page?: string }) => {
    const merged = {
      search,
      key_account: filters.key_account ?? "all",
      research: filters.research ?? "",
      status: filters.status ?? "",
      sort_by: filters.sort_by,
      sort_order: filters.sort_order,
      ...next,
    }
    const query: Record<string, string> = {}
    Object.entries(merged).forEach(([k, v]) => {
      if (v != null && v !== "") query[k] = String(v)
    })
    router.get("/admin/accounts", query, { preserveState: true, preserveScroll: true, replace: true })
  }

  const onSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters({ search })
  }

  const seedAccounts = () => {
    if (!confirm("Sinkronkan daftar akun dari master outlet? Akun baru akan ditambahkan; akun yang ada tetap dipertahankan.")) return
    router.post("/admin/accounts/seed", {}, { preserveScroll: true })
  }

  const totalPages = Math.max(1, Math.ceil(pagination.total_count / pagination.per_page))

  return (
    <>
      <Head title="Account">
        <meta name="description" content={DESCRIPTION} />
        <meta property="og:title" content="Account" />
        <meta property="og:description" content={DESCRIPTION} />
      </Head>
      <AppShell>
        <div className="mb-2 flex items-center gap-1.5 text-xs text-ink-muted">
          <span>Outlet</span>
          <span>/</span>
          <span className="text-ink-body font-medium">Account</span>
        </div>

        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h1>Account</h1>
              <p className="mt-1 text-sm text-ink-muted">{DESCRIPTION}</p>
            </div>
            <Button variant="secondary" onClick={seedAccounts}>
              <RefreshCw className="mr-2 h-4 w-4" />
              Sinkronkan dari Outlet
            </Button>
          </div>
          <div className="mt-4 flex flex-wrap gap-4 text-sm text-ink-muted">
            <span>{stats.total} akun</span>
            <span className="inline-flex items-center gap-1">
              <Star className="h-3.5 w-3.5" /> {stats.key_accounts} Key Account
            </span>
            <span>{stats.archived} diarsipkan</span>
          </div>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <form onSubmit={onSearchSubmit} className="flex-1 min-w-[200px]">
            <label htmlFor="search">Cari</label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
              <Input
                id="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Nama, kode, atau outlet group"
                className="pl-9"
              />
            </div>
          </form>
          <div className="min-w-[150px]">
            <label htmlFor="key_account">Key Account</label>
            <Select
              id="key_account"
              className="mt-1"
              value={filters.key_account ?? "all"}
              onChange={(e) => applyFilters({ key_account: e.target.value })}
            >
              <option value="all">Semua channel</option>
              <option value="true">Key Account</option>
              <option value="false">Non-KA</option>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label htmlFor="research">Riset</label>
            <Select
              id="research"
              className="mt-1"
              value={filters.research ?? ""}
              onChange={(e) => applyFilters({ research: e.target.value })}
            >
              <option value="">Semua</option>
              <option value="true">Aktif</option>
              <option value="false">Nonaktif</option>
            </Select>
          </div>
          <div className="min-w-[150px]">
            <label htmlFor="status">Status</label>
            <Select
              id="status"
              className="mt-1"
              value={filters.status ?? ""}
              onChange={(e) => applyFilters({ status: e.target.value })}
            >
              <option value="">Semua</option>
              <option value="active">Aktif</option>
              <option value="archived">Diarsipkan</option>
            </Select>
          </div>
        </div>

        {filters.key_account === "true" && (
          <p className="mt-3 text-xs text-ink-muted">
            Menampilkan <strong>Key Account</strong> secara default. Pilih{" "}
            <strong>Semua channel</strong> pada filter Key Account untuk melihat semua akun.
          </p>
        )}

        {/* List */}
        {accounts.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">
              Belum ada akun. Klik <strong>Sinkronkan dari Outlet</strong> untuk menurunkan daftar akun dari master outlet.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Nama</th>
                    <th className="px-4 py-3 font-medium">Kode</th>
                    <th className="px-4 py-3 font-medium">Outlet Group</th>
                    <th className="px-4 py-3 font-medium">PIC</th>
                    <th className="px-4 py-3 font-medium">Penanda</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link href={`/admin/accounts/${a.id}`} className="font-medium">
                          {a.name}
                        </Link>
                        {a.account_group_name && (
                          <div className="text-xs text-ink-muted">{a.account_group_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-ink-body">{a.code ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-muted">{a.outlet_group_key}</td>
                      <td className="px-4 py-3 text-ink-body">{a.pic_name ?? "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {a.is_key_account && (
                            <Badge tone="accent">
                              <Star className="mr-1 h-3 w-3" />KA
                            </Badge>
                          )}
                          {a.research_enabled && (
                            <Badge tone="signal">
                              <Telescope className="mr-1 h-3 w-3" />Riset
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge tone={a.status === "archived" ? "muted" : "success"}>
                          {a.status === "archived" ? "Diarsipkan" : "Aktif"}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {accounts.map((a) => (
                <Link
                  key={a.id}
                  href={`/admin/accounts/${a.id}`}
                  className="rounded-lg border border-hairline bg-surface p-4"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium text-ink-body">{a.name}</div>
                      <div className="text-xs text-ink-muted">{a.outlet_group_key}</div>
                    </div>
                    <Badge tone={a.status === "archived" ? "muted" : "success"}>
                      {a.status === "archived" ? "Diarsipkan" : "Aktif"}
                    </Badge>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {a.code && <Badge tone="neutral">{a.code}</Badge>}
                    {a.is_key_account && <Badge tone="accent">KA</Badge>}
                    {a.research_enabled && <Badge tone="signal">Riset</Badge>}
                  </div>
                  {a.pic_name && <div className="mt-2 text-xs text-ink-muted">PIC: {a.pic_name}</div>}
                </Link>
              ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between text-sm text-ink-muted">
            <span>
              Halaman {pagination.page} dari {totalPages} ({pagination.total_count} akun)
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page <= 1}
                onClick={() => applyFilters({ page: String(pagination.page - 1) })}
              >
                Sebelumnya
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={pagination.page >= totalPages}
                onClick={() => applyFilters({ page: String(pagination.page + 1) })}
              >
                Berikutnya
              </Button>
            </div>
          </div>
        )}
      </AppShell>
    </>
  )
}
